/**
 * Projects, as the app has them: the numbers across the machine, then one section per project
 * with a row per repository (its name, where it lives, its branch and remote, its host in words).
 * The project open on the workbench is marked and carries the live figures (agents, open tasks,
 * approvals, the base branch); the others are as the app or the platform last saw them.
 *
 * The list is every project this device registered with the platform, merged with the app's own
 * `projects.list` when it has it and `workspaces.list` as the live overlay (useMachineProjects.ts).
 */

import { BranchesRight, Folder, Globe, Pulse, Server } from '@gravity-ui/icons';

import { ArtEmpty, ErrorNote, ListBlock, ListGroup, ListRow, Stat, StatusMark } from '../../components/ui.tsx';
import { timeAgo } from '../../hooks.ts';
import { useMoney } from '../../money.tsx';
import { hostMark, type MachineProject, type MachineRoot, remoteWords, rootRunning, targetWords } from '../projectsModel.ts';
import { useMachineProjects } from '../useMachineProjects.ts';
import type { GitStatus, Task, Usage, Workbench, Workspace } from '../useWorkbench.ts';
import { RemoteView } from './RemoteView.tsx';

const TONE = 'blue';

interface Figures {
  base?: string;
  agents?: number;
  working?: number;
  openTasks?: number;
  approvals: number;
}

/** The open project's live figures, one line, numbers first as the app's root strip has them. */
function FigureStrip({ f }: { f: Figures }) {
  const item = (value: number | undefined, label: string, needsYou = false) => (
    <div className="flex items-baseline gap-1">
      <dd className={`m-0 text-sm font-semibold tabular-nums ${needsYou ? 'text-warning' : 'text-foreground'}`}>{value ?? '—'}</dd>
      <dt className="text-xs text-muted">{label}</dt>
    </div>
  );
  return (
    <dl className="m-0 flex flex-wrap items-baseline gap-x-5 gap-y-1">
      {f.base && (
        <div className="flex items-center gap-1 text-xs">
          <BranchesRight className="size-3 text-muted" />
          <dd className="mono m-0 font-medium">{f.base}</dd>
          <dt className="text-muted">base</dt>
        </div>
      )}
      {item(f.agents, f.working ? `agents · ${f.working} working` : 'agents')}
      {item(f.openTasks, 'open tasks')}
      {item(f.approvals, f.approvals === 1 ? 'approval waiting' : 'approvals waiting', f.approvals > 0)}
    </dl>
  );
}

function RootRow({ r, showFocus }: { r: MachineRoot; showFocus: boolean }) {
  const mark = hostMark(r);
  const where = targetWords(r);
  const remote = remoteWords(r.remote);
  return (
    <ListRow
      icon={<Folder />}
      title={
        <span title={r.name === r.rootId ? r.name : `${r.name} (${r.rootId})`}>
          {r.name}
          {showFocus && r.workspace?.focused ? <span className="ml-2 text-xs font-normal text-muted">· in focus</span> : null}
        </span>
      }
      status={mark ? <StatusMark state={mark.state} label={mark.label} /> : undefined}
      description={
      <span className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-0.5">
        {where && <span>{where}</span>}
        {r.branch && (
          <span className="flex items-center gap-1 text-foreground">
            <BranchesRight className="size-3 text-muted" />
            <span className="mono">{r.branch}</span>
          </span>
        )}
        {remote ? (
          <span className="flex min-w-0 items-center gap-1" title={r.remote}>
            <Globe className="size-3 shrink-0" />
            <span className="truncate">{remote}</span>
          </span>
        ) : null}
        {r.workspace?.root && (
          <span className="mono min-w-0 truncate" title={r.workspace.root}>
            {r.workspace.root}
          </span>
        )}
        {!r.live && r.lastSeenAt && <span>last running {timeAgo(r.lastSeenAt)}</span>}
      </span>
      }
    />
  );
}

function ProjectSection({ p, figures }: { p: MachineProject; figures?: Figures }) {
  return (
    <ListGroup title={p.name} count={p.roots.length} description={[p.open ? 'Open on the workbench now' : undefined, p.organisation ? undefined : 'Only on this machine'].filter(Boolean).join(' · ') || undefined}>
      {figures ? (
        <ListBlock>
          <FigureStrip f={figures} />
        </ListBlock>
      ) : null}
      {p.roots.map((r) => (
        <RootRow key={r.rootId} r={r} showFocus={p.open && p.roots.length > 1} />
      ))}
    </ListGroup>
  );
}

/** Repositories the workbench has open that no project names: listed so nothing live goes missing. */
function UnmatchedSection({ rows }: { rows: Workspace[] }) {
  return (
    <ListGroup title="Open on the workbench" count={rows.length} description="Repositories with a running host that no project here names.">
      {rows.map((ws) => {
        const ready = (ws.state ?? 'ready') === 'ready';
        return (
          <ListRow
            key={ws.id}
            icon={<Folder />}
            title={
              <>
                {ws.name}
                {ws.focused ? <span className="ml-2 text-xs font-normal text-muted">· in focus</span> : null}
              </>
            }
            description={
              <span className="flex min-w-0 flex-wrap items-center gap-x-3">
                {targetWords(ws) && <span>{targetWords(ws)}</span>}
                {ws.root && (
                  <span className="mono min-w-0 truncate" title={ws.root}>
                    {ws.root}
                  </span>
                )}
              </span>
            }
            status={<StatusMark state={ready ? 'connected' : 'offline'} label={ready ? 'Running' : 'Stopped'} />}
          />
        );
      })}
    </ListGroup>
  );
}

export function ProjectsView({ w }: { w: Workbench }) {
  const { format } = useMoney();
  const a = w.activity.data;
  const m = useMachineProjects(w);
  const today = w.useView<Usage>('usage.history', { days: 1 }, [], 60_000);
  const teamId = w.status.data?.teams[0]?.id ?? 'solo';
  const tasks = w.useView<Task[]>('board.list', { teamId }, [teamId], 30_000);
  const git = w.useView<GitStatus | null>('git.status', undefined, [], 60_000);

  const roots = m.projects.flatMap((p) => p.roots);
  const repositories = roots.length + m.unmatched.length;
  const running = roots.filter(rootRunning).length + m.unmatched.filter((ws) => (ws.state ?? 'ready') === 'ready').length;
  const figures: Figures = {
    base: git.data?.base ?? git.data?.branches?.find((b) => b.kind === 'base')?.name,
    agents: a?.agents.length,
    working: a?.working,
    openTasks: tasks.data ? tasks.data.filter((t) => t.status !== 'done').length : undefined,
    approvals: (a?.pendingApprovals ?? 0) + (a?.openDecisions ?? 0),
  };
  const nothing = !m.loading && m.projects.length === 0 && m.unmatched.length === 0;
  const known = !m.loading || m.projects.length > 0;

  return (
    <RemoteView narrow>
      <div className="art-stats">
        <Stat tone="blue" icon={<Folder />} label="Projects" value={known ? m.projects.length : undefined} detail={known ? `${repositories} ${repositories === 1 ? 'repository' : 'repositories'}` : ''} />
        <Stat tone="teal" icon={<Server />} label="Hosts running" value={w.connected && known ? running : undefined} detail={w.connected ? `of ${repositories}` : 'the workbench is not connected'} />
        <Stat tone="green" icon={<Pulse />} label="Spend today" value={today.data ? format(today.data.totals.costUsd) : undefined} detail="on this machine" />
      </div>
      <ErrorNote error={m.error} />
      {m.projects.map((p) => (
        <ProjectSection key={p.id} p={p} figures={p.open && w.connected ? figures : undefined} />
      ))}
      {m.unmatched.length > 0 && <UnmatchedSection rows={m.unmatched} />}
      {m.loading && m.projects.length === 0 && <p className="text-sm text-muted">Reading this machine's projects…</p>}
      {nothing && !m.error && (
        <ArtEmpty tone={TONE} icon={<Folder />} title="No projects from this machine">
          This machine has not registered a project yet; the workbench registers its projects when it starts.
        </ArtEmpty>
      )}
    </RemoteView>
  );
}
